import React from 'react';
import {
  ArrowForwardIcon,
  Button,
  CheckIcon,
  Text,
  type IconComponent,
} from '@billda/design-system';

interface OnboardingChecklistItemProps {
  step: number;
  title: string;
  description: string;
  icon: IconComponent;
  statusText: string;
  isComplete: boolean;
  ctaLabel: string;
  onCtaClick: () => void;
  isLast?: boolean;
  'data-testid'?: string;
}

export const OnboardingChecklistItem: React.FC<
  OnboardingChecklistItemProps
> = ({
  step,
  title,
  description,
  icon: StepIcon,
  statusText,
  isComplete,
  ctaLabel,
  onCtaClick,
  isLast = false,
  'data-testid': testId,
}) => {
  // Stagger each step: 0.3s base + 0.1s per step
  const delay = `${0.3 + (step - 1) * 0.1}s`;

  return (
    <div
      data-testid={testId}
      className={`relative flex gap-4 opacity-0 ${
        isLast ? 'pb-0' : 'pb-2'
      } animate-[gsob-fade-slide-up_0.4s_ease-out_both]`}
      style={{ animationDelay: delay }}
    >
      {/* Timeline column */}
      <div className="flex w-9 shrink-0 flex-col items-center">
        {/* Node */}
        <div
          className={`flex size-9 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-300 ${
            isComplete
              ? 'border-surface-success bg-surface-success text-on-success'
              : 'border-border-default bg-transparent text-content-secondary'
          }`}
        >
          {isComplete ? (
            <CheckIcon
              aria-hidden="true"
              className="size-[18px]"
              data-testid={`${testId}-complete-icon`}
            />
          ) : (
            <Text
              as="span"
              variant="caption"
              className="text-[0.8rem] font-bold"
              data-testid={`${testId}-incomplete-icon`}
            >
              {step}
            </Text>
          )}
        </div>

        {/* Connecting line */}
        {!isLast && (
          <div
            className={`mt-1 w-0.5 flex-1 transition-colors duration-300 ${
              isComplete ? 'bg-surface-success opacity-40' : 'bg-border-default'
            }`}
          />
        )}
      </div>

      {/* Content */}
      <div className={`min-w-0 flex-1 ${isLast ? 'pb-0' : 'pb-6'}`}>
        <div className="flex min-h-9 items-center gap-2">
          <StepIcon
            aria-hidden="true"
            className={`size-[18px] transition-colors duration-300 ${
              isComplete
                ? 'text-content-success opacity-70'
                : 'text-content-accent'
            }`}
          />
          <Text
            as="span"
            variant="body2"
            className={`flex-1 font-semibold ${
              isComplete ? 'text-content-secondary' : 'text-content-primary'
            }`}
          >
            {title}
          </Text>
          <Text
            as="span"
            variant="caption"
            className={`shrink-0 font-medium ${
              isComplete ? 'text-content-success' : 'text-content-disabled'
            }`}
          >
            {statusText}
          </Text>
        </div>

        <Text
          as="div"
          variant="caption"
          color="secondary"
          className={`mt-0.5 block leading-normal ${
            isComplete ? 'opacity-60' : 'opacity-85'
          }`}
        >
          {description}
        </Text>

        {!isComplete && (
          <Button
            variant="neutral"
            size="sm"
            className="mt-2 text-xs font-semibold"
            onClick={onCtaClick}
            data-testid={`${testId}-cta`}
          >
            {ctaLabel}
            <ArrowForwardIcon aria-hidden="true" className="size-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
};
